import axios from "axios";

// ---------------------------------------------------------------------------
// Backend URL resolution
// ---------------------------------------------------------------------------
// REACT_APP_BACKEND_URL must be injected at BUILD time (Create React App bakes
// it into the bundle). On Railway / Vercel / Netlify, set it as a service env
// variable BEFORE the build step runs. If it's missing we still fall back to
// window.location.origin so the app doesn't crash, but we emit a loud console
// warning so the misconfiguration is impossible to miss.
// ---------------------------------------------------------------------------
const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RUNTIME_ORIGIN =
  typeof window !== "undefined" && window.location ? window.location.origin : "";

export const BACKEND_URL =
  (RAW_BACKEND_URL || "https://api.vibe2nite.com").replace(/\/+$/, "");

if (typeof window !== "undefined" && !RAW_BACKEND_URL) {
  // eslint-disable-next-line no-console
  console.warn(
    "[v2n] REACT_APP_BACKEND_URL was not set at build time. " +
      "Falling back to window.location.origin (" +
      RUNTIME_ORIGIN +
      "). API calls will hit the frontend host and likely 404. " +
      "Fix: set REACT_APP_BACKEND_URL in your hosting provider's env vars and re-run the build."
  );
} else if (
  typeof window !== "undefined" &&
  process.env.NODE_ENV !== "production" &&
  BACKEND_URL === RUNTIME_ORIGIN
) {
  // Only warn in development. In production deployments (Emergent preview,
  // Vercel rewrites, Railway with same-origin /api ingress, etc.) it is
  // perfectly valid for the backend to share an origin with the frontend.
  // eslint-disable-next-line no-console
  console.warn(
    "[v2n] Resolved BACKEND_URL equals window.location.origin (" +
      RUNTIME_ORIGIN +
      "). Fine if your backend is reverse-proxied on the same domain; " +
      "double-check REACT_APP_BACKEND_URL otherwise."
  );
}

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: 15000,
  withCredentials: true,
});

export const getAdminSession = () =>
  api.get("/admin/me").then((response) => response.data);

let cachedCsrfToken = null;
let cachedSalesCsrfToken = null;

async function getAdminCsrfToken() {
  if (cachedCsrfToken) return cachedCsrfToken;

  const response = await axios.get(`${BACKEND_URL}/api/admin/csrf`, {
    withCredentials: true,
  });

  cachedCsrfToken = response.data.csrf_token;
  return cachedCsrfToken;
}

async function adminCsrfConfig() {
  const csrfToken = await getAdminCsrfToken();

  return {
    headers: {
      "X-CSRF-Token": csrfToken,
    },
  };
}

async function getSalesCsrfToken() {
  if (cachedSalesCsrfToken) return cachedSalesCsrfToken;

  const response = await api.get("/sales/csrf");
  cachedSalesCsrfToken = response.data.csrf_token;
  return cachedSalesCsrfToken;
}

async function salesCsrfConfig() {
  const csrfToken = await getSalesCsrfToken();

  return {
    headers: {
      "X-CSRF-Token": csrfToken,
    },
  };
}

// ---------------------------------------------------------------------------
// PLACEHOLDER DATA (for when backend returns null / empty)
// ---------------------------------------------------------------------------
const PLACEHOLDER_VIBES = {
  best_overall: {
    venue_name: "La Terraza Rooftop",
    vibe_score: 9.2,
    tagline: "Rooftop · Cocktails",
    distance: "6 min away"
  },
  live_music: {
    venue_name: "El Pimpi Bar",
    vibe_score: 8.7,
    tagline: "Live Music · Tapas",
    distance: "12 min away"
  },
  hidden_gem: {
    venue_name: "Local Tapas Spot",
    vibe_score: 8.4,
    tagline: "Tapas · Wine",
    distance: "4 min away"
  }
};

export const getTopVibes = async (lat, lng, radius_km = 50) => {
  try {
    const r = await api.get("/vibes/top", {
      params: { lat, lng, radius_km }
    });

    return r.data;
  } catch (err) {
    console.error("API failed, using placeholder vibes");

    return {
      best_overall: {
        venue: {
          id: "1",
          name: "Test Club",
          image_url: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=1200&auto=format&fit=crop",
          latitude: lat,
          longitude: lng,
          is_verified: true,
          category: "club"
        },
        vibe: { vibe_score: 8.5 },
        distance_km: 1
      },
      live_music: {
        venue: {
          id: "2",
          name: "Live Bar",
          image_url: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?q=80&w=1200&auto=format&fit=crop",
          latitude: lat,
          longitude: lng,
          is_verified: true,
          category: "live_music"
        },
        vibe: { vibe_score: 7.8 },
        distance_km: 2
      },
      hidden_gem: {
        venue: {
          id: "3",
          name: "Hidden Spot",
          image_url: "https://images.unsplash.com/photo-1496024840928-4c417adf211d?q=80&w=1200&auto=format&fit=crop",
          latitude: lat,
          longitude: lng,
          is_verified: false,
          category: "bar"
        },
        vibe: { vibe_score: 7.2 },
        distance_km: 0.5
      }
    };
  }
};

export const submitFeedback = (venue_id, vote) =>
  api.post("/feedback", { venue_id, vote }).then((r) => r.data);

export const listAdminVenues = () => api.get("/admin/venues").then((r) => r.data);

export const listAdminSessions = () =>
  api.get("/admin/sessions").then((r) => r.data);

export const revokeAdminSession = async (sessionId) =>
  api
    .post(`/admin/sessions/${sessionId}/revoke`, {}, await adminCsrfConfig())
    .then((r) => r.data);

export const revokeOtherAdminSessions = async () =>
  api
    .post("/admin/sessions/revoke-others", {}, await adminCsrfConfig())
    .then((r) => r.data);

export const createVenue = async (payload) =>
  api.post("/admin/venues", payload, await adminCsrfConfig()).then((r) => r.data);

export const updateVenue = async (venue_id, payload) =>
  api.patch(`/admin/venues/${venue_id}`, payload, await adminCsrfConfig()).then((r) => r.data);

export const updateSignals = async (venue_id, signals) =>
  api.patch(`/admin/venues/${venue_id}/signals`, signals, await adminCsrfConfig()).then((r) => r.data);

export const triggerSignalRefresh = async () =>
  api.post("/admin/signals/refresh", {}, await adminCsrfConfig()).then((r) => r.data);

export const getForecast = (venue_id) =>
  api.get("/vibes/forecast", { params: venue_id ? { venue_id } : {} }).then((r) => r.data);

export const getTouristFlags = () =>
  api.get("/vibes/tourist-flags").then((r) => r.data);

export const getLiveMusic = () =>
  api.get("/vibes/live-music").then((r) => r.data);

export const getHeatmap = () =>
  api.get("/vibes/heatmap").then((r) => r.data);

// --- Iteration 13: claims, providers, webhooks -----------------------------
export const submitClaim = (payload) =>
  api.post("/claims/submit", payload).then((r) => r.data);

export const listClaims = (status) =>
  api.get("/admin/claims", { params: status ? { status } : {} }).then((r) => r.data);

export const reviewClaim = async (claim_id, action, reviewer = "admin", note = "") =>
  api.post(`/admin/claims/${claim_id}/review`, { action, reviewer, note }, await adminCsrfConfig()).then((r) => r.data);

export const getVenueOwner = (venue_id) =>
  api.get(`/venues/${venue_id}/owner`).then((r) => r.data);

export const getProviderStatus = () =>
  api.get("/admin/providers/status").then((r) => r.data);

export const getRecentWebhooks = (limit = 20) =>
  api.get("/admin/webhooks/recent", { params: { limit } }).then((r) => r.data);

// --- Iteration 14: owner-scoped dashboard ----------------------------------
const ownerHeaders = (key) => ({ headers: { "X-Owner-Key": key } });

export const getOwnerMe = (key) =>
  api.get("/owner/me", ownerHeaders(key)).then((r) => r.data);

export const getOwnerInbox = (key, limit = 20) =>
  api.get("/owner/inbox", { ...ownerHeaders(key), params: { limit } }).then((r) => r.data);

export const setOwnerHandles = (key, payload) =>
  api.put("/owner/venue/handles", payload, ownerHeaders(key)).then((r) => r.data);

// --- Iteration 15: multi-venue + owner webhooks ----------------------------
export const getOwnerVenues = (key) =>
  api.get("/owner/venues", ownerHeaders(key)).then((r) => r.data);

export const setOwnerHandlesForVenue = (key, venue_id, payload) =>
  api.put(`/owner/venue/${venue_id}/handles`, payload, ownerHeaders(key)).then((r) => r.data);

export const setOwnerWebhooks = (key, venue_id, payload) =>
  api.put(`/owner/venue/${venue_id}/webhooks`, payload, ownerHeaders(key)).then((r) => r.data);

export const testOwnerWebhook = (key, venue_id) =>
  api.post(`/owner/venue/${venue_id}/webhooks/test`, {}, ownerHeaders(key)).then((r) => r.data);

// --- Iteration 16: ownership transfer / expire / reverify / first-visit ----
export const requestOwnerTransfer = (key, venue_id, email) =>
  api.post(`/owner/venue/${venue_id}/transfer/request`, { email }, ownerHeaders(key)).then((r) => r.data);

export const acceptOwnerTransfer = (token) =>
  api.get(`/owner/transfer/accept/${token}`).then((r) => r.data);

export const adminExpireOwnership = async (venue_id, reviewer = "admin") =>
  api.post(`/admin/venue/${venue_id}/ownership/expire`, null, {
    ...(await adminCsrfConfig()),
    params: { reviewer },
  }).then((r) => r.data);
export const uploadAdminEventImage = async (file) => {
  const form = new FormData();
  form.append("file", file);

  return api
    .post("/admin/events/upload-image", form, await adminCsrfConfig())
    .then((r) => r.data);
};
// --- Admin events ----------------------------------------------------------
export const listAdminEvents = ({
  status,
  event_type,
  venue_id,
} = {}) =>
  api
    .get("/admin/events", {
      params: {
        ...(status ? { status } : {}),
        ...(event_type ? { event_type } : {}),
        ...(venue_id ? { venue_id } : {}),
      },
    })
    .then((r) => r.data);

export const createAdminEvent = async (payload) =>
  api
    .post("/admin/events", payload, await adminCsrfConfig())
    .then((r) => r.data);

export const updateAdminEvent = async (event_id, payload) =>
  api
    .patch(`/admin/events/${event_id}`, payload, await adminCsrfConfig())
    .then((r) => r.data);

export const approveAdminEvent = async (event_id) =>
  api
    .post(`/admin/events/${event_id}/approve`, {}, await adminCsrfConfig())
    .then((r) => r.data);

export const expireAdminEvent = async (event_id) =>
  api
    .post(`/admin/events/${event_id}/expire`, {}, await adminCsrfConfig())
    .then((r) => r.data);

export const deleteAdminEvent = async (event_id) =>
  api
    .delete(`/admin/events/${event_id}`, await adminCsrfConfig())
    .then((r) => r.data);
// --- SEO venue discovery admin --------------------------------------------
export const discoverSeoVenues = async (payload) =>
  api
    .post("/seo/venues/discover", payload, await adminCsrfConfig())
    .then((r) => r.data);

export const listSeoDiscoveryQueue = ({
  city = "Benalmadena",
  status = "pending",
  limit = 100,
} = {}) =>
  api
    .get("/seo/venues/discovery-queue", {
      params: { city, status, limit },
    })
    .then((r) => r.data);

export const approveSeoDiscovery = async (discovery_id) =>
  api
    .post(
      "/seo/venues/approve-discovered",
      { discovery_id },
      await adminCsrfConfig()
    )
    .then((r) => r.data);

export const rejectSeoDiscovery = async (discovery_id) =>
  api
    .post(
      "/seo/venues/reject-discovered",
      { discovery_id },
      await adminCsrfConfig()
    )
    .then((r) => r.data);
export const submitReverify = (payload) =>
  api.post("/claims/reverify", payload).then((r) => r.data);

export const checkInVenue = (venue_id, device_id) =>
  api.post("/intel/visits/check-in", { venue_id, device_id }).then((r) => r.data);


// --- Iteration 17: rewards / referrals ------------------------------------
export const earnReward = (user_id, action, amount) =>
  api
    .post("/rewards/earn", { user_id, action, ...(amount ? { amount } : {}) })
    .then((r) => r.data);


  export const getWallet = async (user_id) => {
  try {
    const res = await api.get(`/rewards/wallet/${encodeURIComponent(user_id)}`);
    return res.data;
  } catch (e) {
    console.warn("Wallet temporarily disabled");
    return { credits: 0, referral_credits: 0 };
  }
};

// --- Public SEO venues -----------------------------------------------------
export const listSeoVenues = (city = "Benalmadena") =>
  api.get("/seo/venues", { params: { city } }).then((r) => r.data);
// --- Agent / SEO operations ------------------------------------------------
export const listPendingDiscoveryQueue = (city = "Benalmadena", limit = 100) =>
  api
    .get("/seo/venues/discovery-queue", {
      params: {
        city,
        status: "pending",
        limit,
      },
    })
    .then((r) => r.data);
export const refreshSeoVenueImage = async ({ name, city = "Benalmadena" }) =>
  api
    .post(
      "/seo/venues/enrich",
      {
        name,
        city,
        refresh: true,
      },
      await adminCsrfConfig()
    )
    .then((r) => r.data);

// --- Admin additional venue photos -----------------------------------------
export const listVenueAdditionalPhotos = (venue_id) =>
  api.get(`/admin/venues/${venue_id}/additional-photos`).then((r) => r.data);

export const uploadVenueAdditionalPhoto = async (
  venue_id,
  { file, caption = "", photo_type = "other", display_order = 0 }
) => {
  const form = new FormData();
  form.append("file", file);
  form.append("caption", caption);
  form.append("photo_type", photo_type);
  form.append("display_order", String(display_order ?? 0));

  return api
    .post(
      `/admin/venues/${venue_id}/additional-photos`,
      form,
      await adminCsrfConfig()
    )
    .then((r) => r.data);
};

export const updateVenueAdditionalPhoto = async (venue_id, photo_id, payload) =>
  api
    .patch(
      `/admin/venues/${venue_id}/additional-photos/${photo_id}`,
      payload,
      await adminCsrfConfig()
    )
    .then((r) => r.data);

export const setVenuePrimaryPhoto = async (venue_id, photo_id) =>
  api
    .post(
      `/admin/venues/${venue_id}/additional-photos/${photo_id}/primary`,
      {},
      await adminCsrfConfig()
    )
    .then((r) => r.data);

export const deleteVenueAdditionalPhoto = async (venue_id, photo_id) =>
  api
    .delete(
      `/admin/venues/${venue_id}/additional-photos/${photo_id}`,
      await adminCsrfConfig()
    )
    .then((r) => r.data);
// --- Public awards ---------------------------------------------------------
export const listAwards = ({ location, year } = {}) =>
  api
    .get("/awards", {
      params: {
        ...(location ? { location } : {}),
        ...(year ? { year } : {}),
      },
    })
    .then((r) => r.data);

export const getAward = (slug) =>
  api.get(`/awards/${encodeURIComponent(slug)}`).then((r) => r.data);

export const listAwardsHallOfFame = ({ location, year } = {}) =>
  api
    .get("/awards/hall-of-fame", {
      params: {
        ...(location ? { location } : {}),
        ...(year ? { year } : {}),
      },
    })
    .then((r) => r.data);

// --- Admin venue analytics -------------------------------------------------
export const listAdminVenueAnalytics = ({
  start_date,
  end_date,
}) =>
  api
    .get("/admin/analytics/venues", {
      params: {
        start_date,
        end_date,
      },
    })
    .then((response) => response.data);
// --- Anonymous venue engagement tracking -----------------------------------
export const trackVenueAnalytics = (payload) =>
  api
    .post("/analytics/track", payload, {
      timeout: 3000,
    })
    .then((response) => response.data)
    .catch(() => null);

// --- Admin venue outreach --------------------------------------------------
export const getVenueOutreachDashboard = ({
  outreach_status,
  package_status,
  follow_up_due = false,
  limit = 25,
} = {}) =>
  api
    .get("/admin/intelligence/venue-outreach/dashboard", {
      params: {
        ...(outreach_status ? { outreach_status } : {}),
        ...(package_status ? { package_status } : {}),
        follow_up_due,
        limit,
      },
    })
    .then((r) => r.data);

export const getVenueOutreachRecord = (venue_id) =>
  api
    .get(`/admin/intelligence/venue-outreach/venues/${encodeURIComponent(venue_id)}/record`)
    .then((r) => r.data);

export const getOrCreateVenueOutreachRecord = async (venue_id) =>
  api
    .post(
      `/admin/intelligence/venue-outreach/venues/${encodeURIComponent(venue_id)}/record`,
      {},
      await adminCsrfConfig()
    )
    .then((r) => r.data);

export const updateVenueOutreachStatus = async (venue_id, payload) =>
  api
    .patch(
      `/admin/intelligence/venue-outreach/venues/${encodeURIComponent(venue_id)}/status`,
      payload,
      await adminCsrfConfig()
    )
    .then((r) => r.data);

export const updateVenueOutreachNotes = async (venue_id, payload) =>
  api
    .patch(
      `/admin/intelligence/venue-outreach/venues/${encodeURIComponent(venue_id)}/notes`,
      payload,
      await adminCsrfConfig()
    )
    .then((r) => r.data);

export const updateVenueOutreachFollowUp = async (venue_id, payload) =>
  api
    .patch(
      `/admin/intelligence/venue-outreach/venues/${encodeURIComponent(venue_id)}/follow-up`,
      payload,
      await adminCsrfConfig()
    )
    .then((r) => r.data);

export const updateVenueOutreachContactMethod = async (venue_id, payload) =>
  api
    .patch(
      `/admin/intelligence/venue-outreach/venues/${encodeURIComponent(venue_id)}/preferred-contact-method`,
      payload,
      await adminCsrfConfig()
    )
    .then((r) => r.data);

export const updateVenueOutreachPackage = async (venue_id, payload) =>
  api
    .patch(
      `/admin/intelligence/venue-outreach/venues/${encodeURIComponent(venue_id)}/package`,
      payload,
      await adminCsrfConfig()
    )
    .then((r) => r.data);

export const updateVenueOutreachDoNotContact = async (venue_id, payload) =>
  api
    .patch(
      `/admin/intelligence/venue-outreach/venues/${encodeURIComponent(venue_id)}/do-not-contact`,
      payload,
      await adminCsrfConfig()
    )
    .then((r) => r.data);

export const createVenueOutreachHistory = async (venue_id, payload) =>
  api
    .post(
      `/admin/intelligence/venue-outreach/venues/${encodeURIComponent(venue_id)}/history`,
      payload,
      await adminCsrfConfig()
    )
    .then((r) => r.data);

export const listVenueOutreachHistory = ({ venue_id, limit = 50 }) =>
  api
    .get(`/admin/intelligence/venue-outreach/venues/${encodeURIComponent(venue_id)}/history`, {
      params: { limit },
    })
    .then((r) => r.data);

// --- Admin venue intelligence ---------------------------------------------
export const getVenueIntelligence = (venue_id) =>
  api
    .get(`/admin/venue-intelligence/${encodeURIComponent(venue_id)}`)
    .then((r) => r.data);

export const updateVenueIntelligenceAttribute = async (venue_id, payload) =>
  api
    .patch(
      `/admin/venue-intelligence/${encodeURIComponent(venue_id)}/attributes`,
      payload,
      await adminCsrfConfig()
    )
    .then((r) => r.data);

// --- Sales Portal ----------------------------------------------------------
export const salesLogin = async (payload) => {
  const response = await api.post("/sales/login", payload);
  cachedSalesCsrfToken = response.data?.csrf_token || null;
  return response.data;
};

export const salesLogout = async () => {
  cachedSalesCsrfToken = null;
  return api.post("/sales/logout").then((r) => r.data);
};

export const getSalesMe = () => api.get("/sales/me").then((r) => r.data);

export const getSalesDashboard = () => api.get("/sales/dashboard").then((r) => r.data);

export const listSalesLeads = (params = {}) =>
  api.get("/sales/leads", { params }).then((r) => r.data);

export const getSalesLead = (lead_id) =>
  api.get(`/sales/leads/${encodeURIComponent(lead_id)}`).then((r) => r.data);

export const updateSalesLead = async (lead_id, payload) =>
  api
    .patch(`/sales/leads/${encodeURIComponent(lead_id)}`, payload, await salesCsrfConfig())
    .then((r) => r.data);

export const createSalesActivity = async (lead_id, payload) =>
  api
    .post(`/sales/leads/${encodeURIComponent(lead_id)}/activities`, payload, await salesCsrfConfig())
    .then((r) => r.data);

export const startSalesVisit = async (lead_id, payload = {}) =>
  api
    .post(`/sales/leads/${encodeURIComponent(lead_id)}/visits/start`, payload, await salesCsrfConfig())
    .then((r) => r.data);

export const completeSalesVisit = async (visit_id, payload) =>
  api
    .patch(`/sales/visits/${encodeURIComponent(visit_id)}/complete`, payload, await salesCsrfConfig())
    .then((r) => r.data);

export const createSalesFollowUp = async (lead_id, payload) =>
  api
    .post(`/sales/leads/${encodeURIComponent(lead_id)}/follow-ups`, payload, await salesCsrfConfig())
    .then((r) => r.data);

export const updateSalesFollowUp = async (follow_up_id, payload) =>
  api
    .patch(`/sales/follow-ups/${encodeURIComponent(follow_up_id)}`, payload, await salesCsrfConfig())
    .then((r) => r.data);

export const upsertSalesPilot = async (lead_id, payload) =>
  api
    .post(`/sales/leads/${encodeURIComponent(lead_id)}/pilot`, payload, await salesCsrfConfig())
    .then((r) => r.data);

export const getSalesPack = () => api.get("/sales/sales-pack").then((r) => r.data);

// --- Admin Sales Command Centre -------------------------------------------
export const getAdminSalesOverview = () =>
  api.get("/admin/sales/overview").then((r) => r.data);

export const listAdminSalesUsers = () =>
  api.get("/admin/sales/users").then((r) => r.data);

export const createAdminSalesUser = async (payload) =>
  api.post("/admin/sales/users", payload, await adminCsrfConfig()).then((r) => r.data);

export const updateAdminSalesUser = async (user_id, payload) =>
  api
    .patch(`/admin/sales/users/${encodeURIComponent(user_id)}`, payload, await adminCsrfConfig())
    .then((r) => r.data);

export const listAdminSalesLeads = (params = {}) =>
  api.get("/admin/sales/leads", { params }).then((r) => r.data);

export const createAdminSalesLead = async (payload) =>
  api.post("/admin/sales/leads", payload, await adminCsrfConfig()).then((r) => r.data);

export const getAdminSalesLead = (lead_id) =>
  api.get(`/admin/sales/leads/${encodeURIComponent(lead_id)}`).then((r) => r.data);

export const updateAdminSalesLead = async (lead_id, payload) =>
  api
    .patch(`/admin/sales/leads/${encodeURIComponent(lead_id)}`, payload, await adminCsrfConfig())
    .then((r) => r.data);

export const assignAdminSalesLead = async (lead_id, sales_user_id) =>
  api
    .post(
      `/admin/sales/leads/${encodeURIComponent(lead_id)}/assign`,
      { sales_user_id },
      await adminCsrfConfig()
    )
    .then((r) => r.data);

export const listAdminSalesFollowUps = (params = {}) =>
  api.get("/admin/sales/follow-ups", { params }).then((r) => r.data);


// --- Admin event ingestion -------------------------------------------------
export const getEventIngestionAutomation = () =>
  api.get("/admin/event-ingestion/automation").then((r) => r.data);

export const updateEventIngestionAutomation = async (enabled) =>
  api.patch("/admin/event-ingestion/automation", { enabled }, await adminCsrfConfig()).then((r) => r.data);

export const getEventIngestionSources = () =>
  api.get("/admin/event-ingestion/sources").then((r) => r.data);

export const getEventIngestionFreshness = () =>
  api.get("/admin/event-ingestion/freshness").then((r) => r.data);

export const getEventCoverage = (params = {}) =>
  api.get("/admin/event-ingestion/coverage", { params }).then((r) => r.data);

export const createEventCoverageDraftSource = async (candidateId) =>
  api.post(
    "/admin/event-ingestion/coverage/draft-source",
    { candidate_id: candidateId },
    await adminCsrfConfig()
    ).then((r) => r.data);

export const validateEventCoverageSource = async (candidateId) =>
  api.post(
    "/admin/event-ingestion/coverage/validate",
    { candidate_id: candidateId },
    await adminCsrfConfig()
  ).then((r) => r.data);

export const validateTopEventCoverageSources = async (limit = 5) =>
  api.post(
    "/admin/event-ingestion/coverage/validate-top",
    { limit },
    await adminCsrfConfig()
  ).then((r) => r.data);

export const submitManualFacebookEvidence = async (payload) =>
  api.post("/admin/event-ingestion/manual-facebook-evidence", payload, await adminCsrfConfig()).then((r) => r.data);

export const submitEventIntakeBatch = async ({ files, caption = "", sourceUrl = "", processMode = "review_only" }) => {
  const form = new FormData();
  files.forEach((file) => form.append("files", file));
  form.append("caption", caption);
  form.append("process_mode", processMode);
  if (sourceUrl) form.append("source_url", sourceUrl);
  return api.post("/admin/event-ingestion/intake-batches", form, await adminCsrfConfig()).then((r) => r.data);
};

export const getEventIntakeBatches = (limit = 10) =>
  api.get("/admin/event-ingestion/intake-batches", { params: { limit } }).then((r) => r.data);

export const getEventIntakeBatch = (batchId) =>
  api.get(`/admin/event-ingestion/intake-batches/${encodeURIComponent(batchId)}`).then((r) => r.data);

export const createEventIngestionSource = async (payload) =>
  api.post("/admin/event-ingestion/sources", payload, await adminCsrfConfig()).then((r) => r.data);

export const updateEventIngestionSource = async (sourceId, payload) =>
  api.patch(`/admin/event-ingestion/sources/${encodeURIComponent(sourceId)}`, payload, await adminCsrfConfig()).then((r) => r.data);

export const seedEventIngestionSources = async () =>
  api.post("/admin/event-ingestion/sources/seed-starters", {}, await adminCsrfConfig()).then((r) => r.data);

export const getEventCandidates = (params = {}) =>
  api.get("/admin/event-ingestion/candidates", { params }).then((r) => r.data);

export const confirmEventCandidateOccurrence = async (candidateId, payload) =>
  api.patch(`/admin/event-ingestion/candidates/${encodeURIComponent(candidateId)}/occurrence`, payload, await adminCsrfConfig()).then((r) => r.data);

export const correctEventCandidateDetails = async (candidateId, payload) =>
  api.patch(`/admin/event-ingestion/candidates/${encodeURIComponent(candidateId)}/details`, payload, await adminCsrfConfig()).then((r) => r.data);

export const approveEventCandidate = async (candidateId, payload = {}) =>
  api.post(`/admin/event-ingestion/candidates/${encodeURIComponent(candidateId)}/approve`, payload, await adminCsrfConfig()).then((r) => r.data);

export const createManualEventIntakeItem = async () =>
  api.post("/admin/event-ingestion/intake-items/manual-draft", {}, await adminCsrfConfig()).then((r) => r.data);

export const previewManualEventIntakeItem = async (itemId, payload) =>
  api.post(`/admin/event-ingestion/intake-items/${encodeURIComponent(itemId)}/manual-preview`, payload, await adminCsrfConfig()).then((r) => r.data);

export const publishManualEventIntakeItem = async (itemId, payload) =>
  api.post(`/admin/event-ingestion/intake-items/${encodeURIComponent(itemId)}/manual-publish`, payload, await adminCsrfConfig()).then((r) => r.data);

export const rejectEventCandidate = async (candidateId, reason) =>
  api.post(`/admin/event-ingestion/candidates/${encodeURIComponent(candidateId)}/reject`, { reason }, await adminCsrfConfig()).then((r) => r.data);

export const runEventIngestionSource = async (sourceId) =>
  api.post(`/admin/event-ingestion/sources/${encodeURIComponent(sourceId)}/run`, {}, await adminCsrfConfig()).then((r) => r.data);

export const runEventIngestion = async () =>
  api.post("/admin/event-ingestion/run", {}, await adminCsrfConfig()).then((r) => r.data);

export const getEventIngestionRuns = () =>
  api.get("/admin/event-ingestion/runs").then((r) => r.data);

export const getLocalEventResearch = (params) =>
  api.get("/admin/event-ingestion/local-research", { params }).then((r) => r.data);

export const startLocalEventResearch = async (payload) =>
  api.post("/admin/event-ingestion/local-research", payload, { ...(await adminCsrfConfig()), timeout: 240000 }).then((r) => r.data);

export const linkEventCandidateVenue = async (candidateId, venueId) =>
  api.patch(`/admin/event-ingestion/candidates/${encodeURIComponent(candidateId)}/venue`, { venue_id: venueId }, await adminCsrfConfig()).then((r) => r.data);

export const getEventCandidatePreview = (id) => api.get(`/admin/event-ingestion/candidates/${encodeURIComponent(id)}/preview`).then(r => r.data);

export const previewBulkEventText = async (text, dateContext = {}) =>
  api.post('/admin/event-ingestion/bulk-text/preview', { text, ...dateContext }, await adminCsrfConfig()).then(r => r.data);
export const reviewBulkEventText = async (items) =>
  api.post('/admin/event-ingestion/bulk-text/review', { items }, await adminCsrfConfig()).then(r => r.data);
export const importBulkEventText = async (items) =>
  api.post('/admin/event-ingestion/bulk-text/import', { items }, await adminCsrfConfig()).then(r => r.data);


